import React, { useEffect, useState, useRef, useContext } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitSharpIcon from '@mui/icons-material/FullscreenExitSharp';
import Chat from '../components/chat/Chat';
import PeopleDrawer from '../components/presentation-components/PeopleDrawer';
import LeftPaneSection from '../components/presentation-components/LeftPaneSection';
import PresentationDrawer from '../components/presentation-components/PresentationDrawer';
import Vyur from '../components/presentation-components/Vyur';
import RoomLayout from '../components/presentation-components/RoomLayout';
import GuestService from '../services/GuestService';
import EventManager from '../Util/EventManager';
import { Link, useNavigate } from 'react-router-dom';
import MessageContext from '../App/Contexts/MessageContext';
import useFullscreen from '../Util/custom-hooks/useFullscreen';
import useSlides from '../Util/custom-hooks/useSlides';

// Guest counterpart of AppHeader: same bar and room-name breadcrumb, but no
// menu/avatar (there is no logged-in user) and a "Viewing as guest" label
// where the member view shows its room actions.
function GuestHeader({ roomName }) {
    return (
        <Box
            sx={(theme) => ({
                background: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                padding: '4px 14px',
                display: 'flex',
                alignItems: 'center',
                minHeight: '48px'
            })}>
            <Typography variant='h5' noWrap sx={roomName ? { display: { xs: 'none', sm: 'block' } } : undefined}>
                <Link to='/welcome' style={{ color: 'unset', textDecoration: 'unset' }}>
                    VYU1
                </Link>
            </Typography>
            {roomName ? (
                <Typography
                    variant='h6'
                    noWrap
                    sx={{ ml: { xs: 0, sm: 2 }, fontWeight: 'bolder', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {roomName}
                </Typography>
            ) : null}
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant='body2' sx={{ opacity: 0.8, whiteSpace: 'nowrap' }}>
                Viewing as guest
            </Typography>
        </Box>
    );
}

export default function GuestRoom({ roomUrl, displayName }) {
    const navigate = useNavigate();
    const { setMessage } = useContext(MessageContext);
    const [thisRoom, setThisRoom] = useState({});
    const { fullscreen, toggleFullscreen } = useFullscreen();

    const sessionIdRef = useRef(null);
    const eventSourceRef = useRef(null);

    const {
        setItems,
        currentImg,
        updateImage,
        getSlides,
        loadingSlides,
        sortAsc,
        toggleSort,
        getPresentationLoadedProgress
    } = useSlides();

    // Heartbeat polling — fetches room state and updates slides
    useEffect(() => {
        let cancelled = false;
        let interval;

        function sendHeartbeat() {
            const previousSessionId = sessionIdRef.current;
            GuestService.heartbeat(roomUrl, previousSessionId, displayName)
                .then((response) => {
                    if (cancelled) {
                        // Effect was cleaned up before this response arrived.
                        // If this call just created a new session, terminate it
                        // so a remount (e.g. StrictMode) doesn't leave it orphaned.
                        if (!previousSessionId && response.sessionId) {
                            GuestService.leave(roomUrl, response.sessionId);
                        }
                        return;
                    }
                    sessionIdRef.current = response.sessionId;
                    setThisRoom(response.room);
                    setItems(response.room.presentationItems, (item) =>
                        GuestService.getImageBlob(roomUrl, item.itemName)
                    );
                })
                .catch((e) => {
                    if (cancelled) return;
                    if (e?.response?.status === 404) {
                        // Room is gone (e.g. deleted by the host) — mirrors how
                        // PresentationRoom handles a "not found" roomLogin: stop
                        // polling, tell the guest, and send them away. The guest
                        // session outlives the room server-side, so end it here,
                        // then null the ref so the unmount cleanups don't repeat it.
                        cancelled = true;
                        clearInterval(interval);
                        if (sessionIdRef.current) {
                            GuestService.leave(roomUrl, sessionIdRef.current);
                            sessionIdRef.current = null;
                        }
                        setMessage('Room not found');
                        navigate('/home');
                        return;
                    }
                    // Transient/network errors (no response, 5xx) shouldn't kick
                    // the guest out — keep polling through those.
                    console.error('Heartbeat failed:', e);
                });
        }

        sendHeartbeat();
        interval = setInterval(sendHeartbeat, 5000);
        return () => {
            cancelled = true;
            clearInterval(interval);
            if (sessionIdRef.current) {
                GuestService.leave(roomUrl, sessionIdRef.current);
                sessionIdRef.current = null;
            }
        };
    }, [roomUrl, displayName, setItems, navigate, setMessage]);

    // Open SSE channel for trail events once we have a session in a room.
    // Mirrors the EventSource setup in App.js for logged-in users, scoped
    // to the room rather than to a userId.
    useEffect(() => {
        if (!thisRoom.roomId) return;
        // Same-render guard against double-opening; StrictMode remount opens a fresh
        // connection per cycle (server assigns a unique key to each).
        if (eventSourceRef.current) return;
        const sid = sessionIdRef.current;
        if (!sid) return;

        const eventsHost = process.env.REACT_APP_EVENTS_HOST || '';
        const url = `${eventsHost}/events/room/${thisRoom.roomId}/${sid}`;
        const es = new EventSource(url);
        eventSourceRef.current = es;

        const trailListener = (e) => EventManager.emitPathEvent(e);
        es.addEventListener('PresentationTrailEvent', trailListener);
        es.onerror = (e) => console.log('Guest event source error', e);

        return () => {
            es.removeEventListener('PresentationTrailEvent', trailListener);
            es.close();
            eventSourceRef.current = null;
        };
    }, [thisRoom.roomId]);

    // Cleanup on unmount / tab close
    useEffect(() => {
        function handleBeforeUnload() {
            if (sessionIdRef.current) {
                GuestService.leave(roomUrl, sessionIdRef.current);
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (sessionIdRef.current) {
                GuestService.leave(roomUrl, sessionIdRef.current);
            }
        };
    }, [roomUrl]);

    const guestRoomData = {
        ...thisRoom,
        roommates: thisRoom.roommates || []
    };

    const renderVyur = ({ fullscreen: isFullscreen }) => (
        <Vyur
            img={currentImg}
            roommateIds={[]}
            roomId={thisRoom.roomId}
            fullscreen={isFullscreen}
            isAdmin={false}
            readOnly
            hudActions={[
                {
                    text: 'Full Screen',
                    icon: fullscreen ? <FullscreenExitSharpIcon /> : <FullscreenRoundedIcon />,
                    onClick: toggleFullscreen
                }
            ]}
        />
    );

    const renderPresentationDrawer = (horizontal) => (
        <PresentationDrawer
            imgClick={updateImage}
            onSort={toggleSort}
            slides={getSlides()}
            loadingSlides={loadingSlides}
            sortAsc={sortAsc}
            roomId={thisRoom.roomId}
            onDelete={() => {}}
            adminIds={[]}
            roomOwnerId={''}
            horizontal={horizontal}
            readOnly
            {...getPresentationLoadedProgress()}
        />
    );

    const peoplePanel = <PeopleDrawer roomData={guestRoomData} />;

    // Guest chat messages carry sender names but no ids, so admins are matched
    // by name for the chat panel's admin-coloured avatars.
    const adminNames = guestRoomData.roommates
        .filter((r) => r.role === 'ADMIN')
        .map((r) => `${r.user.firstName} ${r.user.lastName}`);

    const chat = (
        <Chat
            roomId={thisRoom.roomId}
            readOnly
            messages={thisRoom.chatMessages || []}
            adminNames={adminNames}
        />
    );

    // Server-computed guest visibility (Presentation Mode + per-feature
    // settings). Missing fields fail open for compatibility; the loading gate
    // prevents these defaults from rendering before the first heartbeat.
    const canSeeChat = thisRoom.chatVisible !== false;
    const canSeeImageQ = thisRoom.imageQVisible !== false;

    // Mobile bottom panel: only show tabs for visible features, so a hidden
    // Chat or Image Q drops its tab entirely (People is always shown).
    const mobileTabs = [];
    if (canSeeChat) {
        mobileTabs.push({ label: 'Chat', content: chat });
    }
    mobileTabs.push({ label: 'People', content: <Box sx={{ p: 2 }}>{peoplePanel}</Box> });
    if (canSeeImageQ) {
        mobileTabs.push({ label: 'Slides', content: renderPresentationDrawer(true) });
    }

    const leftPane = (
        <LeftPaneSection title="People" id="people-panel-header">
            {peoplePanel}
        </LeftPaneSection>
    );

    return (
        <RoomLayout
            loading={!thisRoom?.name}
            fullscreen={fullscreen}
            renderVyur={renderVyur}
            header={<GuestHeader roomName={thisRoom.name} />}
            leftPane={leftPane}
            chat={canSeeChat ? chat : null}
            rightPane={canSeeImageQ ? renderPresentationDrawer(false) : null}
            mobileTabs={mobileTabs}
        />
    );
}
